import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { assetUrl } from "../adminApi";
import { useI18n } from "../i18n";
import { trackEvent } from "../tracker";

export default function CollectionCard({ item, fallbackPdf = "", index = 0, subCount = 0 }) {
  const { t, loc } = useI18n();
  const gallery = (item.gallery && item.gallery.length ? item.gallery : [item.cover]).filter(Boolean);
  const [active, setActive] = useState(0);
  const pdf = item.pdf_url || fallbackPdf;
  const flip = index % 2 === 1;
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          trackEvent("collection", item.key, { once: true });
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [item.key]);

  return (
    <article
      ref={ref}
      className="grid grid-cols-1 lg:grid-cols-12 bg-white border border-[#E2E0D8]"
      data-testid={`collection-card-${item.key}`}
    >
      <div className={`lg:col-span-7 bg-[#EFECE6] ${flip ? "lg:order-2" : ""}`}>
        <div className="media aspect-[4/3] overflow-hidden">
          <img
            src={assetUrl(gallery[active] || gallery[0])}
            alt={`${item.name} composition`}
            className="w-full h-full object-cover"
          />
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto" data-testid={`collection-gallery-${item.key}`}>
            {gallery.map((g, i) => (
              <button
                key={`${g}-${i}`}
                onClick={() => setActive(i)}
                aria-label={`${item.name} image ${i + 1}`}
                className={`shrink-0 w-[78px] h-[58px] overflow-hidden border transition-colors duration-300 ${
                  i === active ? "border-[#141414]" : "border-[#E2E0D8] hover:border-[#C8B29B]"
                }`}
              >
                <img src={assetUrl(g)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="lg:col-span-5 p-7 lg:p-11 flex flex-col justify-center">
        <p className="mono text-[9px] text-[#6B675F]">
          {item.no} · {loc(item.type).toUpperCase()}
        </p>
        <h3 className="display text-[32px] sm:text-[42px] lg:text-[52px] text-[#141414] mt-3 leading-[0.98]">
          {item.name}
        </h3>
        <p className="text-[16px] lg:text-[19px] text-[#141414] mt-5 leading-snug max-w-[460px]">
          {loc(item.headline)}
        </p>
        <p className="text-[14px] text-[#141414]/65 mt-5 leading-relaxed max-w-[520px]">
          {loc(item.character)}
        </p>

        <dl className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4 border-t border-[#E2E0D8] pt-6">
          {[
            [t("hero.modules"), item.modules],
            [t("hero.panel"), item.panel],
            [t("hero.layouts"), item.layouts],
          ]
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k}>
                <dt className="mono text-[8.5px] text-[#A8A29A]">{k}</dt>
                <dd className="text-[13.5px] text-[#141414] mt-2 leading-snug">{v}</dd>
              </div>
            ))}
        </dl>

        {subCount > 0 && (
          <Link
            to={`/products/${item.key}`}
            data-testid={`collection-subs-${item.key}`}
            className="mt-8 inline-flex items-center gap-2 border border-[#141414] px-5 py-3.5 mono text-[10px] text-[#141414] hover:bg-[#141414] hover:text-[#FAFAFA] transition-colors duration-300 w-fit"
          >
            {t("products.collections")} ({subCount}) <ArrowUpRight size={13} strokeWidth={1.7} />
          </Link>
        )}

        {pdf && (
          <a
            href={assetUrl(pdf)}
            target="_blank"
            rel="noreferrer"
            download
            onClick={() => trackEvent("pdf", item.key)}
            data-testid={`collection-pdf-${item.key}`}
            className="mt-9 inline-flex items-center gap-2.5 bg-[#141414] text-[#FAFAFA] px-6 py-4 mono text-[10px] hover:bg-[#2A2A28] transition-colors duration-300 w-fit"
          >
            <Download size={14} strokeWidth={1.7} /> {t("products.download")}
          </a>
        )}
      </div>
    </article>
  );
}
